import { cx } from "@linaria/core";
import { Popover, usePopoverState } from "reakit/Popover";
import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cssHidden } from "@/styles/utils/Utils";
import { ANIMATED_TRANSITION_DEFAULT, colors } from "@/styles/utils/vars";
import { ClearItemFilterPropsType, CloseModeType } from "@/types";
import { Button } from "@/ui/Button/Button";
import { Icon } from "@/ui/Icon";
import { cssPopoverAngle, StyledPopoverInner } from "@/ui/Popover/StyledPopover";
import { cssBtnClearItem, cssIsActive, cssVariantPopover, cssVariantRelative, PopoverContainerFilter, StyledPopoverDisclosureFilter } from "./StyledFilterPopover";
const matchingVariant = {
  popover: cssVariantPopover,
  relative: cssVariantRelative
};
export type MatchingNameType = keyof typeof matchingVariant;
export const getSelectorByVariantFilter: (variant?: MatchingNameType) => typeof cssVariantPopover = (variant = "popover") => matchingVariant[variant];
export type FilterPopoverType = {
  title: string;
  isActive?: boolean;
  selectedKeys?: string[];
  onClearFilterItem?: ({
    keyFilter,
    checked,
    name
  }: ClearItemFilterPropsType) => void;
  order?: number;
  name?: string;
  onShow?: () => void;
  onHide?: () => void;
  visibleDefault?: boolean;
  isResponsive?: boolean;
  closeMode?: CloseModeType;
  variant?: MatchingNameType;
};
export const FilterPopover: FC<FilterPopoverType> = ({
  title,
  isActive = false,
  selectedKeys = [],
  name,
  onClearFilterItem,
  onShow,
  onHide,
  visibleDefault,
  isResponsive = false,
  closeMode = "hide",
  variant = "popover",
  children
}) => {
  const [isInit, setIsInit] = useState(false);
  const popover = usePopoverState({
    animated: ANIMATED_TRANSITION_DEFAULT,
    gutter: 16,
    placement: "bottom-start",
    unstable_flip: false
  });
  const previousVisibleRef = useRef(!!visibleDefault);
  const clearFilters = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (onClearFilterItem) {
      onClearFilterItem({
        keyFilter: selectedKeys,
        checked: false,
        name: name
      });
    }
  }, [name, onClearFilterItem, selectedKeys]);
  const selectedQtyElement = useMemo(() => isActive && !!selectedKeys?.length && `: ${selectedKeys?.length}`, [isActive, selectedKeys?.length]);
  const isShowClear = useMemo(() => isActive && <Button variant={"link"} icon={"X"} iconSize={"small"} onClick={clearFilters} as={"a"} className={cssBtnClearItem} />, [clearFilters, isActive]);
  const popupVisible = useMemo(() => visibleDefault !== undefined && !isInit ? visibleDefault : popover.visible, [isInit, popover.visible, visibleDefault]);
  useEffect(() => {
    if (previousVisibleRef.current !== popover.visible) {
      if (!isInit) {
        // for responsive visible all
        if (visibleDefault !== undefined && visibleDefault !== popover.visible) {
          popover.setVisible(true);
        }
      } else {
        //default handler
        if (!popover.visible) {
          if (onHide) {
            onHide();
          }
        } else {
          if (onShow) {
            onShow();
          }
        }
        previousVisibleRef.current = popover.visible;
      }
    }
    setIsInit(true);
  }, [popover.visible, isInit, visibleDefault, popover, onHide, onShow]);
  const isVisibleInner = closeMode === "destroy" ? popover.visible : true;
  return <PopoverContainerFilter className={cx(getSelectorByVariantFilter(variant), isActive && cssIsActive)}>
      <StyledPopoverDisclosureFilter {...popover}>
        {title}
        {selectedQtyElement}
        {isShowClear}
        <Icon NameIcon={"AngleBottom"} fill={colors.brand.purple} className={cx(isActive ? cssHidden : "", cssPopoverAngle)} size={"small"} />
      </StyledPopoverDisclosureFilter>
      <Popover {...popover} visible={popupVisible} hideOnClickOutside={!isResponsive} aria-label={name || "popoverFilter"}>
        {isVisibleInner && <StyledPopoverInner>{children}</StyledPopoverInner>}
      </Popover>
    </PopoverContainerFilter>;
};